/**
 * UserAvatar - renders a user's profile picture when available, else a
 * deterministic "animal emoji + color" default derived from user_id.
 *
 * Also falls back to the default if the remote image fails to load (so users
 * whose uploaded image is missing from storage still get a stable avatar).
 */

import React, { useState, useEffect } from 'react';
import {
  getDefaultAvatarEmoji,
  getDefaultAvatarColor,
} from '../../helpers/usersHelpers';

function UserAvatar({
  user,
  className = 'w-10 h-10 rounded-full',
  emojiClassName = 'text-xl',
  imgClassName = '',
  alt,
}) {
  const [failed, setFailed] = useState(false);

  // Reset the failure flag if the url changes (new upload etc.).
  useEffect(() => {
    setFailed(false);
  }, [user?.profile_pic_url]);

  const url = user?.profile_pic_url;
  const userId = user?.user_id;
  const emoji = getDefaultAvatarEmoji(userId);
  const bgColor = user?.default_profile_color || getDefaultAvatarColor(userId);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={alt || user?.displayname || user?.username || ''}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={`${className} object-cover ${imgClassName}`}
      />
    );
  }

  return (
    <div
      className={`${className} flex items-center justify-center select-none`}
      style={{ backgroundColor: bgColor }}
      data-testid="user-avatar-default"
      aria-label={alt || user?.displayname || user?.username || 'User avatar'}
    >
      <span role="img" aria-hidden="true" className={emojiClassName}>
        {emoji}
      </span>
    </div>
  );
}

export default UserAvatar;
